import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  syncThemeFromServer: (serverTheme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // 초기값은 localStorage에서 가져오기
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme || 'dark';
  });

  // API 기본 URL
  const getApiUrlLocal = () => {
    return getApiUrl();
  };

  const applyTheme = (newTheme: Theme) => {
    console.log('🎨 테마 적용 시작:', newTheme);
    
    const root = document.documentElement;
    const body = document.body;
    
    // 모든 기존 테마 클래스 제거
    root.classList.remove('light', 'dark');
    body.classList.remove('light', 'dark');
    
    // 새 테마 클래스 적용
    root.classList.add(newTheme);
    body.classList.add(newTheme);
    
    if (newTheme === 'dark') {
      // 다크 모드 속성 설정
      root.setAttribute('data-theme', 'dark');
      body.setAttribute('data-theme', 'dark');
      
      // CSS 변수 설정
      root.style.setProperty('--bg-color', '#0f1419');
      root.style.setProperty('--text-color', '#ffffff');
      root.style.setProperty('--border-color', '#374151');
      
      // body 스타일 직접 적용
      body.style.backgroundColor = '#0f1419';
      body.style.color = '#ffffff';
      
      console.log('🌙 다크 모드 적용 완료');
    } else {
      // 라이트 모드 속성 설정
      root.setAttribute('data-theme', 'light');
      body.setAttribute('data-theme', 'light');
      
      // CSS 변수 설정
      root.style.setProperty('--bg-color', '#f8fafc');
      root.style.setProperty('--text-color', '#1f2937');
      root.style.setProperty('--border-color', '#e5e7eb');
      
      // body 스타일 직접 적용
      body.style.backgroundColor = '#f8fafc';
      body.style.color = '#1f2937';
      
      console.log('☀️ 라이트 모드 적용 완료');
    }
    
    // 강제로 리렌더링 트리거
    window.dispatchEvent(new Event('themechange'));
    
    // 약간의 지연 후 클래스 확인 및 재적용
    setTimeout(() => {
      if (!root.classList.contains(newTheme)) {
        root.classList.add(newTheme);
        console.log('🔧 테마 클래스 재적용:', newTheme);
      }
    }, 100);
  };

  const syncThemeFromServer = (serverTheme: Theme) => {
    console.log('🔄 서버에서 테마 동기화:', serverTheme);
    setTheme(serverTheme);
  };

  // 서버에서 테마 로드
  const loadThemeFromServer = async () => {
    try {
      const response = await fetch(`${getApiUrlLocal()}/api/settings`);
      if (response.ok) {
        const settings = await response.json();
        if (settings.theme && settings.theme !== theme) {
          console.log('📥 서버에서 테마 로드:', settings.theme);
          setTheme(settings.theme);
        }
      }
    } catch (error) {
      console.error('서버 테마 로드 실패:', error);
    }
  };

  // 테마 변경 시 처리
  useEffect(() => {
    // localStorage에 저장
    localStorage.setItem('theme', theme);
    
    // DOM에 테마 적용
    applyTheme(theme);
  }, [theme]);

  // 컴포넌트 마운트 시 서버에서 테마 로드
  useEffect(() => {
    loadThemeFromServer();
  }, []);

  const toggleTheme = () => {
    console.log('🔄 테마 토글 버튼 클릭됨, 현재 테마:', theme);
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // 서버에 테마 변경 알림
    fetch(`${getApiUrlLocal()}/api/settings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ theme: newTheme }),
    }).catch(error => {
      console.error('테마 설정 저장 실패:', error);
    });
  };

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      setTheme(newTheme);
      // 서버에도 저장
      fetch(`${getApiUrlLocal()}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ theme: newTheme }),
      }).catch(error => {
        console.error('테마 설정 저장 실패:', error);
      });
    },
    toggleTheme,
    syncThemeFromServer
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}; 